import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Paper, Typography, Button, CircularProgress, Alert, List, ListItem, ListItemText, Checkbox } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import authService from '../../services/authService';
import { QUIZ_SERVER_MAP, resolveServerQuizId } from '../../config/quizServerMap';
import quizService from '../../services/quizService';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { getOverrideQuestions } from '../../services/quizEditorStorage';
import { API_ENDPOINTS, buildApiActionUrl } from '../../utils/apiConfig';

interface LoadedQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndexes: number[]; // leer wenn unbekannt
}

interface ResultStats {
  score: number;
  total: number;
  percentage: number;
  newHighscore: boolean;
  serverHighscore: number | null;
}

interface StatsState {
  completed: number;
  totalCorrect: number;
  bestScore: number;
}

type ProgressState = {
  index: number;
  score: number;
  questionOutcomes: Record<number, 'correct' | 'wrong'>;
};

// Local storage helpers
const GLOBAL_JOKER_COUNT_STORAGE_KEY = 'quiz_global_joker_count';

function sanitizeOutcomeMap(input: unknown, maxQuestionCount?: number): Record<number, 'correct' | 'wrong'> {
  if (!input || typeof input !== 'object') return {};
  return Object.fromEntries(
    Object.entries(input as Record<string, unknown>).filter(([key, value]) => {
      const index = Number(key);
      if (!Number.isInteger(index) || index < 0) return false;
      if (typeof maxQuestionCount === 'number' && index >= maxQuestionCount) return false;
      return value === 'correct' || value === 'wrong';
    })
  ) as Record<number, 'correct' | 'wrong'>;
}

function loadLocalProgress(storageKey: string): ProgressState {
  try {
    const raw = localStorage.getItem(`quiz_${storageKey}_progress`);
    if (!raw) return { index: 0, score: 0, questionOutcomes: {} };
    const parsed = JSON.parse(raw);
    const outcomes = sanitizeOutcomeMap(parsed?.questionOutcomes);
    const scoreFromOutcomes = Object.values(outcomes).filter((state) => state === 'correct').length;
    return {
      index: typeof parsed?.index === 'number' ? Math.max(0, Math.floor(parsed.index)) : 0,
      score: typeof parsed?.score === 'number' ? Math.max(scoreFromOutcomes, Math.floor(parsed.score)) : scoreFromOutcomes,
      questionOutcomes: outcomes
    };
  } catch {
    return { index: 0, score: 0, questionOutcomes: {} };
  }
}
function saveLocalProgress(storageKey: string, state: ProgressState) {
  try { localStorage.setItem(`quiz_${storageKey}_progress`, JSON.stringify(state)); } catch {}
}
function loadLocalStats(storageKey: string) {
  try {
    const raw = localStorage.getItem(`quiz_${storageKey}_stats`);
    if (!raw) return { completed: 0, totalCorrect: 0, bestScore: 0 };
    return JSON.parse(raw);
  } catch { return { completed: 0, totalCorrect: 0, bestScore: 0 }; }
}
function saveLocalStats(storageKey: string, state: { completed: number; totalCorrect: number; bestScore: number }) {
  try { localStorage.setItem(`quiz_${storageKey}_stats`, JSON.stringify(state)); } catch {}
}

function loadGlobalJokerCount(): number {
  try {
    const raw = localStorage.getItem(GLOBAL_JOKER_COUNT_STORAGE_KEY);
    if (raw == null) return 1;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.max(0, Math.floor(parsed)) : 1;
  } catch {
    return 1;
  }
}

function saveGlobalJokerCount(value: number): void {
  try {
    localStorage.setItem(GLOBAL_JOKER_COUNT_STORAGE_KEY, String(Math.max(0, Math.floor(value))));
  } catch {}
}

const DynamicQuizPage: React.FC = () => {
  const params = useParams();
  const rawSlug = params.page || '';
  // Erwartet slug Format: mX_uY_x99_quiz oder m5_u99_quiz
  const isQuizSlug = /_quiz$/i.test(rawSlug);
  const storageKey = isQuizSlug ? rawSlug.replace(/_quiz$/i, '') : rawSlug; // ohne _quiz
  const displayTitle = `Quiz – ${storageKey.toUpperCase()}`;
  const { token, refreshProfile, canManageQuiz } = useAuth();
  const { darkMode } = useTheme();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [questions, setQuestions] = React.useState<LoadedQuestion[]>([]);
  const [progress, setProgress] = React.useState(() => loadLocalProgress(storageKey));
  const [stats, setStats] = React.useState(() => loadLocalStats(storageKey));
  const [selectedOptions, setSelectedOptions] = React.useState<number[]>([]);
  const [mode, setMode] = React.useState<'answer' | 'feedback' | 'finished'>('answer');
  const [serverHighscore, setServerHighscore] = React.useState<number | null>(null);
  const [result, setResult] = React.useState<ResultStats | null>(null);
  const [feedback, setFeedback] = React.useState<string | null>(null);
  const [wasCorrect, setWasCorrect] = React.useState<boolean | null>(null);
  const [jokerCount, setJokerCount] = React.useState<number>(() => loadGlobalJokerCount());
  const [reportOpen, setReportOpen] = React.useState(false);
  const [reportSubmitting, setReportSubmitting] = React.useState(false);
  const [reportFile, setReportFile] = React.useState<File | null>(null);
  const [reportPreview, setReportPreview] = React.useState<string | null>(null);
  const reportFileInputRef = React.useRef<HTMLInputElement | null>(null);
  const answeredInCurrentSessionRef = React.useRef<Set<number>>(new Set());

  const fetchJokerBalance = React.useCallback(async (): Promise<number | null> => {
    if (!token) return null;
    try {
      const res = await fetch(buildApiActionUrl('getJokerBalance'), { headers: { Authorization: `Bearer ${token}` } as any });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return null;
      const count = Number(data.jokerCount);
      return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
    } catch {
      return null;
    }
  }, [token]);

  const consumeJoker = React.useCallback(async (): Promise<number | null> => {
    if (!token) return null;
    try {
      const res = await fetch(API_ENDPOINTS.api, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        } as any,
        body: JSON.stringify({ action: 'consumeJoker' })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return null;
      const count = Number(data.jokerCount);
      return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
    } catch {
      return null;
    }
  }, [token]);

  const grantJoker = React.useCallback(async (amount = 1): Promise<number | null> => {
    if (!token || amount <= 0) return null;
    try {
      const res = await fetch(API_ENDPOINTS.api, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        } as any,
        body: JSON.stringify({ action: 'grantQuizJoker', amount })
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) return null;
      const count = Number(data.jokerCount);
      return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : null;
    } catch {
      return null;
    }
  }, [token]);

  // Preview für Report-Bild
  React.useEffect(() => {
    if (!reportFile) { setReportPreview(null); return; }
    const objectUrl = URL.createObjectURL(reportFile);
    setReportPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [reportFile]);

  // Sheet Kandidaten ableiten (ähnlich statische Seiten)
  const sheetCandidates: string[] = React.useMemo(() => {
    const upper = storageKey.toUpperCase(); // m1_u1_x99 -> M1_U1_X99
    const baseX = upper.replace(/_X99$/i, '');
    const baseU = upper.replace(/_U99$/i, '');
    // quiz_X_Y aus storageKey ableiten (mit oder ohne x)
    const mMatch = storageKey.match(/^m(\d+)_u(\d+)(?:_x)?99$/i);
    const quizField = mMatch ? `quiz_${mMatch[1]}_${mMatch[2]}` : undefined;
    const list: string[] = [upper];
    if (baseX && baseX !== upper) list.push(baseX);
    if (baseU && baseU !== upper && baseU !== baseX) list.push(baseU);
    if (quizField) list.push(quizField);
    return list;
  }, [storageKey]);

  const serverQuizId = React.useMemo(() => {
    // Erst explizite Map, sonst Fallback über Resolver
    if (QUIZ_SERVER_MAP[storageKey as keyof typeof QUIZ_SERVER_MAP]) {
      return QUIZ_SERVER_MAP[storageKey as keyof typeof QUIZ_SERVER_MAP];
    }
    return resolveServerQuizId(storageKey, sheetCandidates);
  }, [storageKey, sheetCandidates]);

  // Fragen laden (Excel direkt → JSON Fallback)
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const override = getOverrideQuestions([storageKey, ...sheetCandidates]);
        if (override) {
          const normalized = override.questions.map((q, idx) => ({
            id: `override_${idx}`,
            question: q.question,
            options: q.options,
            correctIndexes: q.correctIndexes?.length ? q.correctIndexes : [0]
          }));
          if (!cancelled) {
            setQuestions(normalized as LoadedQuestion[]);
            if (!normalized.length) setError('Keine Fragen gefunden.');
          }
          return;
        }
        // Direkt Excel laden mit korrekter parseFixedSchema-Logik
        let loaded: any[] = [];
        try {
          // Versuche alle sheetCandidates nacheinander
          for (const candidate of sheetCandidates) {
            const excelQuestions = await quizService.loadQuestionsFromExcel(candidate);
            if (excelQuestions.length > 0) {
              loaded = excelQuestions.map(q => ({
                id: q.id,
                question: q.question,
                options: q.options.slice(0, 4),
                correctIndexes: [q.correctIndex].filter((idx: number) => idx >= 0)
              }));
              console.info(`[DynamicQuizPage] ${storageKey}: ${loaded.length} Fragen aus Excel (Sheet: ${candidate}) geladen.`);
              break; // Erstes passendes Sheet verwenden
            }
          }
        } catch (excelErr: any) {
          console.warn(`[DynamicQuizPage] Excel-Laden fehlgeschlagen: ${excelErr.message}`);
        }
        // Wenn leer → JSON Fallback
        if (!loaded.length) {
          const jsonRes = await fetch(`/data/quizzes/${storageKey}_quiz.json`);
          if (jsonRes.ok) {
            const jsonData = await jsonRes.json();
            if (Array.isArray(jsonData.questions)) {
              loaded = jsonData.questions.map((q:any, idx:number) => {
                const allAnswers = Array.isArray(q.answers) ? q.answers : [];
                const optionsFirst4 = allAnswers.slice(0, 4).map((a:any) => a.text || a.answer || '');
                // Korrekte Antwort innerhalb der ersten vier bestimmen
                const correctIndexesInFirst4 = allAnswers
                  .slice(0, 4)
                  .map((a:any, answerIdx:number) => (a.correct === true ? answerIdx : -1))
                  .filter((answerIdx:number) => answerIdx >= 0);
                return {
                  id: q.id || `json_${idx}`,
                  question: q.prompt || q.question || '',
                  options: optionsFirst4,
                  correctIndexes: correctIndexesInFirst4.length > 0 ? correctIndexesInFirst4 : [0],
                };
              });
            }
          }
        }
        if (!cancelled) {
          setQuestions(loaded as LoadedQuestion[]);
          if (!loaded.length) setError('Keine Fragen gefunden.');
        }
      } catch (e:any) {
        if (!cancelled) setError(e.message || 'Fehler beim Laden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [storageKey, sheetCandidates]);

  React.useEffect(() => {
    let active = true;
    const sync = async () => {
      const serverCount = await fetchJokerBalance();
      if (!active || serverCount == null) return;
      setJokerCount(serverCount);
      saveGlobalJokerCount(serverCount);
    };
    sync();
    return () => { active = false; };
  }, [fetchJokerBalance]);

  React.useEffect(() => {
    if (loading || questions.length === 0) return;
    setProgress((prev) => {
      const safeOutcomes = sanitizeOutcomeMap(prev.questionOutcomes, questions.length);
      const safeScore = Object.values(safeOutcomes).filter((state) => state === 'correct').length;
      const maxIndex = Math.max(0, questions.length - 1);
      const safeIndex = Math.min(Math.max(prev.index, 0), maxIndex);
      if (
        safeIndex !== prev.index ||
        safeScore !== prev.score ||
        JSON.stringify(safeOutcomes) !== JSON.stringify(prev.questionOutcomes || {})
      ) {
        const next = { index: safeIndex, score: safeScore, questionOutcomes: safeOutcomes };
        saveLocalProgress(storageKey, next);
        return next;
      }
      return prev;
    });
  }, [loading, questions.length, storageKey]);

  React.useEffect(() => {
    if (loading || questions.length === 0 || mode === 'finished') return;
    const safeIndex = Math.min(Math.max(progress.index, 0), questions.length - 1);
    const outcome = progress.questionOutcomes?.[safeIndex];
    if (!outcome) return;

    const answeredInSession = answeredInCurrentSessionRef.current.has(safeIndex);
    if (!answeredInSession) {
      if (safeIndex + 1 < questions.length) {
        const next = { ...progress, index: safeIndex + 1 };
        setProgress(next);
        saveLocalProgress(storageKey, next);
        setSelectedOptions([]);
        setFeedback(null);
        setWasCorrect(null);
        setMode('answer');
        return;
      }

      setMode('finished');
      setFeedback(outcome === 'correct' ? 'Letzte Frage bereits gewertet.' : 'Letzte Frage bereits als falsch gewertet.');
      return;
    }

    if (mode === 'answer') {
      setWasCorrect(outcome === 'correct');
      setFeedback(outcome === 'correct' ? 'Richtig!' : 'Falsch.');
      setMode('feedback');
    }
  }, [loading, mode, progress, questions.length, storageKey]);

  // Highscore bidirektional synchronisieren (Server ↔ Lokal)
  React.useEffect(() => {
    let cancelled = false;
    async function syncHighscore() {
      if (!token || !serverQuizId || loading) return;
      try {
        // 1. Server-Highscore abrufen
        const res = await fetch(buildApiActionUrl('profile'), { headers: { Authorization: `Bearer ${token}` } as any });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const serverScore = typeof data[serverQuizId] === 'number' ? data[serverQuizId] : 0;
        
        if (cancelled) return;
        
        // 2. Lokaler Bestwert
        const localBest = stats.bestScore;
        
        // 4. Lokalen bestScore aktualisieren falls Server höher
        if (serverScore > localBest) {
          setStats((prev: StatsState) => ({ ...prev, bestScore: serverScore }));
          setServerHighscore(serverScore);
        } else {
          setServerHighscore(serverScore);
        }
        
        // 5. Server aktualisieren falls lokal höher
        if (localBest > serverScore) {
          try {
            await authService.updateUser(token, { [serverQuizId]: localBest });
            await refreshProfile();
          } catch (e) {
            console.warn('Highscore-Upload fehlgeschlagen:', e);
          }
        }
      } catch (e) {
        console.warn('Highscore-Sync fehlgeschlagen:', e);
      }
    }
    syncHighscore();
    return () => { cancelled = true; };
  }, [token, serverQuizId, loading, stats.bestScore, refreshProfile]);

  const safeProgressIndex = questions.length > 0 ? Math.min(Math.max(progress.index, 0), questions.length - 1) : 0;
  const current = questions[safeProgressIndex];
  const currentOutcome = progress.questionOutcomes?.[safeProgressIndex];
  const canUseJoker =
    mode === 'feedback' &&
    wasCorrect === false &&
    Boolean(currentOutcome) &&
    answeredInCurrentSessionRef.current.has(safeProgressIndex) &&
    jokerCount > 0;

  function handleSelect(idx: number) {
    if (mode !== 'answer' || Boolean(currentOutcome)) return;
    setSelectedOptions((prev) =>
      prev.includes(idx) ? prev.filter((item) => item !== idx) : [...prev, idx]
    );
  }

  async function handleSubmit() {
    if (!current || selectedOptions.length === 0) return;
    if (progress.questionOutcomes?.[safeProgressIndex]) return;
    const correctIndexes = current.correctIndexes?.length ? current.correctIndexes : [0];
    const selectedSorted = [...selectedOptions].sort((a, b) => a - b);
    const correctSorted = [...correctIndexes].sort((a, b) => a - b);
    const correct =
      selectedSorted.length === correctSorted.length &&
      selectedSorted.every((value, idx) => value === correctSorted[idx]);
    const nextOutcomes: Record<number, 'correct' | 'wrong'> = {
      ...(progress.questionOutcomes || {}),
      [safeProgressIndex]: correct ? 'correct' : 'wrong'
    };
    const newScore = Object.values(nextOutcomes).filter((state) => state === 'correct').length;
    const nextIndex = safeProgressIndex + 1;
    const finished = nextIndex >= questions.length;
    const nextProgress: ProgressState = {
      index: safeProgressIndex,
      score: newScore,
      questionOutcomes: nextOutcomes
    };
    setProgress(nextProgress);
    saveLocalProgress(storageKey, nextProgress);

    setWasCorrect(correct);
    setFeedback(correct ? 'Richtig!' : 'Falsch.');
    setMode('feedback');
    answeredInCurrentSessionRef.current.add(safeProgressIndex);

    if (finished) {
      const percentage = questions.length ? (newScore / questions.length) * 100 : 0;
      const newStats = { ...stats, completed: stats.completed + 1, totalCorrect: stats.totalCorrect + newScore, bestScore: Math.max(stats.bestScore, Math.round(percentage)) };
      setStats(newStats); saveLocalStats(storageKey, newStats);
      const newHighscore = Math.round(percentage) > (serverHighscore || 0);
      setMode('finished');
      setResult({ score: newScore, total: questions.length, percentage, newHighscore, serverHighscore });
      const localNextJoker = Math.max(0, jokerCount) + 1;
      setJokerCount(localNextJoker);
      saveGlobalJokerCount(localNextJoker);
      const serverJoker = await grantJoker(1);
      if (serverJoker != null) {
        setJokerCount(serverJoker);
        saveGlobalJokerCount(serverJoker);
      }
      // Optional zum Server schicken
      if (token && serverQuizId && newHighscore) {
        sendHighscoreToServer(serverQuizId, Math.round(percentage));
      }
    }
  }

  function handleNextAfterFeedback() {
    if (!current) { setMode('answer'); return; }
    const nextIndex = safeProgressIndex + 1;
    const finished = nextIndex >= questions.length;
    const newProgress: ProgressState = { ...progress, index: nextIndex };
    setProgress(newProgress);
    saveLocalProgress(storageKey, newProgress);
    setSelectedOptions([]);
    setFeedback(null);
    setWasCorrect(null);
    setMode(finished ? 'finished' : 'answer');
  }

  async function handleUseJoker() {
    if (!canUseJoker || !current) return;
    const localNext = Math.max(0, jokerCount - 1);
    setJokerCount(localNext);
    saveGlobalJokerCount(localNext);

    const serverNext = await consumeJoker();
    if (serverNext != null) {
      setJokerCount(serverNext);
      saveGlobalJokerCount(serverNext);
    }

    const nextOutcomes = { ...(progress.questionOutcomes || {}) };
    delete nextOutcomes[safeProgressIndex];
    const nextProgress: ProgressState = {
      ...progress,
      questionOutcomes: nextOutcomes,
      score: Object.values(nextOutcomes).filter((state) => state === 'correct').length
    };
    setProgress(nextProgress);
    saveLocalProgress(storageKey, nextProgress);
    answeredInCurrentSessionRef.current.delete(safeProgressIndex);
    setSelectedOptions([]);
    setFeedback(null);
    setWasCorrect(null);
    setMode('answer');
  }

  // Erfolgsquote wie Android
  const totalQuestions = questions.length;
  const successRate = stats.completed > 0 && totalQuestions > 0 ? (stats.totalCorrect / (totalQuestions * stats.completed)) * 100 : 0;

  // Fall melden: Frage-Report an FAQ (submitFaqQuestion)
  async function handleReportSubmit() {
    if (!token || !current) { setReportOpen(false); return; }
    setReportSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('action', 'submitFaqQuestion');
      const text = `Quiz ${storageKey.toUpperCase()} – Frage ${progress.index + 1}\n\n${current.question}`;
      formData.append('question_text', text);
            if (reportFile) {
              formData.append('image_file', reportFile);
            }
      const res = await fetch(API_ENDPOINTS.api, { method: 'POST', headers: { Authorization: `Bearer ${token}` } as any, body: formData });
      await res.json().catch(() => ({}));
    } catch {}
    setReportSubmitting(false);
    setReportOpen(false);
    setReportFile(null);
    setReportPreview(null);
    if (reportFileInputRef.current) reportFileInputRef.current.value = '';
  }

  async function sendHighscoreToServer(field: string, value: number) {
    try {
      await authService.updateUser(token!, { [field]: value });
      await refreshProfile();
      setServerHighscore(value);
    } catch (e) { /* ignore */ }
  }

  const [showResetConfirm, setShowResetConfirm] = React.useState(false);
  function handleResetConfirmed() {
    const p: ProgressState = { index: 0, score: 0, questionOutcomes: {} }; setProgress(p); saveLocalProgress(storageKey, p);
    setMode('answer'); setSelectedOptions([]); setResult(null); setFeedback(null); setWasCorrect(null);
    answeredInCurrentSessionRef.current.clear();
    setShowResetConfirm(false);
  }
  function handleResetRequest() { setShowResetConfirm(true); }
  function handleResetCancel() { setShowResetConfirm(false); }

  if (!isQuizSlug) {
    return React.createElement(Alert, { severity: 'info' }, 'Diese Seite scheint kein Quiz-Slug zu sein.');
  }

  return React.createElement(UnitPageShell, {
    title: displayTitle,
    actions: canManageQuiz
      ? React.createElement(
          Button as any,
          {
            component: RouterLink,
            to: `/admin/quiz-editor?quiz=${encodeURIComponent(storageKey.toUpperCase())}`,
            variant: 'outlined'
          } as any,
          'Quiz bearbeiten'
        )
      : undefined
  },
    loading && React.createElement(Box, { sx: { p: 4, textAlign: 'center' } }, React.createElement(CircularProgress, null)),
    !loading && error && React.createElement(Alert, { severity: 'error' }, error),
    !loading && !error && questions.length > 0 && React.createElement(Paper, { sx: { ...getCardSx(darkMode), p: 3 } },
      React.createElement(Box, { sx: { display:'flex', gap:2, justifyContent:'space-between', mb:2 } },
        React.createElement(Typography, { variant:'subtitle2', sx:{ fontWeight:600 } }, `Highscore: ${serverHighscore ?? stats.bestScore}%`),
        React.createElement(Typography, { variant:'subtitle2', sx:{ fontWeight:600 } }, `Erfolgsquote: ${successRate.toFixed(2)}%`)
      ),
      mode === 'answer' && React.createElement(Box, { sx: { mb: 2 } },
        React.createElement(Typography, { variant: 'h6', sx: { fontWeight: 600 } }, `Frage ${progress.index + 1} von ${questions.length}`),
        React.createElement(Typography, { variant: 'subtitle1', sx: { mb: 2 } }, current?.question || ''),
        React.createElement(List, null,
          current?.options.map((opt, idx) => {
            const isSelected = selectedOptions.includes(idx);
            return React.createElement(ListItem, { key: idx, onClick: () => handleSelect(idx), sx: { borderRadius: 1, mb: 1, border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider', cursor: 'pointer', bgcolor: isSelected ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } } },
              React.createElement(Checkbox, { checked: isSelected, onChange: () => handleSelect(idx), sx: { mr: 1 } }),
              React.createElement(ListItemText, { primary: opt })
            );
          })
        ),
        React.createElement(Box, { sx: { display: 'flex', gap: 2, mt: 2, justifyContent: 'space-between', flexWrap: 'wrap' } },
          React.createElement(Button, { variant: 'outlined', color: 'secondary', onClick: handleResetRequest }, 'Reset'),
          React.createElement(Box, { sx: { display: 'flex', alignItems: 'stretch', gap: 1.25, ml: 'auto' } },
            React.createElement(
              'span',
              {
                title: jokerCount > 0 ? `Joker verfuegbar (${jokerCount})` : 'Kein Joker verfuegbar',
                style: { display: 'inline-flex' }
              },
              React.createElement(
                Button,
                {
                  variant: canUseJoker ? 'contained' : 'outlined',
                  onClick: canUseJoker ? handleUseJoker : () => {},
                  sx: {
                    width: 86,
                    minWidth: 86,
                    height: 64,
                    p: 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 0.3
                  }
                },
                React.createElement('img', {
                  src: '/assets/joker.svg',
                  alt: 'Joker',
                  width: 34,
                  height: 34,
                  style: {
                    opacity: jokerCount > 0 ? 1 : 0.45,
                    filter: jokerCount > 0 ? 'none' : 'grayscale(100%)'
                  }
                }),
                React.createElement(Typography, { sx: { fontWeight: 700, fontSize: '0.78rem', lineHeight: 1 } }, String(jokerCount))
              )
            ),
            React.createElement(Button, { variant: 'contained', disabled: selectedOptions.length === 0 || Boolean(currentOutcome), onClick: handleSubmit }, safeProgressIndex + 1 >= questions.length ? 'Abschließen' : 'Weiter')
          )
        ),
        React.createElement(Typography, { variant: 'caption', sx: { mt: 2, display: 'block' } }, serverQuizId ? `Server-Feld: ${serverQuizId}` : 'Kein Server-Feld erkannt')
      ),
      mode === 'feedback' && React.createElement(Box, { sx: { mb: 2 } },
        React.createElement(Typography, { variant: 'h6', sx: { fontWeight: 600 } }, `Frage ${progress.index + 1} von ${questions.length}`),
        React.createElement(Typography, { variant: 'subtitle1', sx: { mb: 2 } }, current?.question || ''),
        React.createElement(List, null,
          current?.options.map((opt, idx) => {
            const correctIndexes = current?.correctIndexes?.length ? current.correctIndexes : [0];
            const isCorrect = correctIndexes.includes(idx);
            const isSelected = selectedOptions.includes(idx);
            return React.createElement(ListItem, { key: idx, sx: { borderRadius: 1, mb: 1, border: '1px solid', borderColor: isCorrect ? 'success.main' : 'divider', bgcolor: isSelected ? (isCorrect ? 'success.light' : 'error.light') : 'transparent' } },
              React.createElement(Checkbox, { checked: isSelected || isCorrect, disabled: true, sx: { mr: 1 } }),
              React.createElement(ListItemText, { primary: opt })
            );
          })
        ),
        React.createElement(Typography, { variant: 'body1', sx: { mt: 2, mb: 2, fontWeight: 600 } }, feedback || ''),
        wasCorrect === false && React.createElement(Box, { sx: { display: 'flex', mb: 2 } },
          React.createElement(Button, { variant: 'outlined', color:'warning', onClick: () => setReportOpen(true) }, 'Fall melden')
        ),
        React.createElement(Box, { sx: { display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' } },
          React.createElement(Button, { variant: 'outlined', color: 'secondary', onClick: handleResetRequest }, 'Reset'),
          React.createElement(Box, { sx: { display: 'flex', alignItems: 'stretch', gap: 1.25, ml: 'auto' } },
            React.createElement(
              'span',
              {
                title: canUseJoker
                  ? `Joker einsetzen (${jokerCount} verfuegbar)`
                  : jokerCount > 0
                    ? 'Joker nur direkt nach falscher Antwort nutzbar'
                    : 'Kein Joker verfuegbar',
                style: { display: 'inline-flex' }
              },
              React.createElement(
                Button,
                {
                  variant: canUseJoker ? 'contained' : 'outlined',
                  onClick: canUseJoker ? handleUseJoker : () => {},
                  sx: {
                    width: 86,
                    minWidth: 86,
                    height: 64,
                    p: 0.75,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: 0.3
                  }
                },
                React.createElement('img', {
                  src: '/assets/joker.svg',
                  alt: 'Joker',
                  width: 34,
                  height: 34,
                  style: {
                    opacity: jokerCount > 0 ? 1 : 0.45,
                    filter: jokerCount > 0 ? 'none' : 'grayscale(100%)'
                  }
                }),
                React.createElement(Typography, { sx: { fontWeight: 700, fontSize: '0.78rem', lineHeight: 1 } }, String(jokerCount))
              )
            ),
            React.createElement(Button, { variant: 'contained', onClick: handleNextAfterFeedback }, safeProgressIndex + 1 >= questions.length ? 'Zum Ergebnis' : 'Weiter')
          )
        )
      ),
      mode === 'finished' && result && React.createElement(Box, null,
        React.createElement(Typography, { variant: 'h5', sx: { fontWeight: 700, mb: 2 } }, 'Ergebnis'),
        React.createElement(Typography, { variant: 'body1' }, `Richtige Antworten: ${result.score} / ${result.total}`),
        React.createElement(Typography, { variant: 'body1' }, `Prozent: ${result.percentage.toFixed(2)}%`),
        React.createElement(Typography, { variant: 'body1' }, `Server-Highscore: ${result.serverHighscore ?? '—'}%`),
        React.createElement(Typography, { variant: 'body1', sx: { mb: 2 } }, result.newHighscore ? 'Neuer Highscore gespeichert!' : 'Kein neuer Highscore.'),
        React.createElement(Button, { variant: 'contained', onClick: handleResetRequest }, 'Erneut starten')
      ),
      showResetConfirm && React.createElement(Paper, { sx: { position: 'fixed', top: 0, left: 0, right:0, bottom:0, display:'flex', alignItems:'center', justifyContent:'center', bgcolor:'rgba(0,0,0,0.5)', zIndex: 1300 } },
        React.createElement(Paper, { sx: { ...getCardSx(darkMode), p:3, maxWidth: 360 } },
          React.createElement(Typography, { variant:'h6', sx:{ mb:2 } }, 'Quiz zurücksetzen?'),
          React.createElement(Typography, { variant:'body2', sx:{ mb:3 } }, 'Fortschritt dieses Durchgangs geht verloren.'),
          React.createElement(Box, { sx:{ display:'flex', gap:2, justifyContent:'flex-end' } },
            React.createElement(Button, { onClick: handleResetCancel }, 'Abbrechen'),
            React.createElement(Button, { variant:'contained', color:'error', onClick: handleResetConfirmed }, 'Zurücksetzen')
          )
        )
      ),
      reportOpen && React.createElement(Paper, { sx: { position: 'fixed', top: 0, left: 0, right:0, bottom:0, display:'flex', alignItems:'center', justifyContent:'center', bgcolor:'rgba(0,0,0,0.5)', zIndex: 1300 } },
        React.createElement(Paper, { sx: { ...getCardSx(darkMode), p:3, maxWidth: 500 } },
          React.createElement(Typography, { variant:'h6', sx:{ mb:2 } }, 'Fall melden'),
          React.createElement(Typography, { variant:'body2', sx:{ mb:3 } }, `Quiz ${storageKey.toUpperCase()} – Frage ${progress.index + 1}`),
          React.createElement(Typography, { variant:'body2', sx:{ mb:3 } }, current?.question || ''),
                    React.createElement('input', { 
                      ref: reportFileInputRef, 
                      type: 'file', 
                      accept: 'image/*', 
                      hidden: true, 
                      onChange: (e: any) => { const f = e.target?.files?.[0]; setReportFile(f ?? null); } 
                    }),
                    React.createElement(Button, { 
                      variant: 'outlined', 
                      onClick: () => reportFileInputRef.current?.click(),
                      sx: { mb: 2 }
                    }, reportFile ? 'Anderes Bild wählen' : 'Bild auswählen (optional)'),
                    reportPreview && React.createElement('img', { 
                      src: reportPreview, 
                      alt: 'Vorschau', 
                      style: { maxHeight: 200, width: '100%', objectFit: 'contain', marginBottom: 16, borderRadius: 4, border: '1px solid rgba(0,0,0,0.15)' } 
                    }),
          React.createElement(Box, { sx:{ display:'flex', gap:2, justifyContent:'flex-end' } },
            React.createElement(Button, { onClick: () => { setReportOpen(false); setReportFile(null); setReportPreview(null); if(reportFileInputRef.current) reportFileInputRef.current.value=''; } }, 'Abbrechen'),
            React.createElement(Button, { variant:'contained', disabled: reportSubmitting, onClick: handleReportSubmit }, reportSubmitting ? 'Senden…' : 'Senden')
          )
        )
      )
    )
  );
};

export default DynamicQuizPage;

